<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div>
    <div>
      <v-text-field
        :label="i18n.global.t('global.searchTimestamp')"
        readonly
        hide-details
        variant="solo-filled"
        rounded="md"
        :model-value="`${date.format(i18n.global.t('global.dateFormat'))} ${displayDateDay}`"
        :disabled="loadingState === 'loading' || materialStore.loadingState !== 'success'"
        prepend-inner-icon="mdi-calendar"
      >
        <template #append-inner="{ blur }">
          <v-badge :offset-x="-4" :offset-y="-4" dot color="primary" :model-value="labelStore.indicateTimeout">
            <v-btn
              :loading="loadingState === 'loading'"
              @click="
                async () => {
                  blur();
                  getPdfHistory();
                }
              "
              color="primary"
              prepend-icon="mdi-magnify"
              >{{ i18n.global.t("global.search") }}</v-btn
            >
          </v-badge>
        </template>
        <v-menu
          :close-on-content-click="false"
          activator="parent"
          :model-value="loadingState == 'loading' ? false : showDateSelector"
          @update:model-value="(val) => (showDateSelector = val)"
        >
          <template #default="{ isActive }">
            <v-card class="w-min">
              <v-date-picker
                v-model="date"
                :show-adjacent-months="true"
                color="primary"
                :allowed-dates="allowedDates"
                :min="dayjs().subtract(2, 'months').format('YYYY-MM-DD')"
                :max="dayjs().format('YYYY-MM-DD')"
              ></v-date-picker>
              <template #actions>
                <v-badge :offset-x="-4" :offset-y="-4" dot color="primary" :model-value="labelStore.indicateTimeout">
                  <v-btn
                    color="primary"
                    variant="elevated"
                    @click="
                      () => {
                        isActive.value = false;
                        getPdfHistory();
                      }
                    "
                    prepend-icon="mdi-magnify"
                    >{{ i18n.global.t("global.search") }}</v-btn
                  >
                </v-badge>
              </template>
            </v-card>
          </template>
        </v-menu>
      </v-text-field>
      {{ selectedChip }}
      <v-chip-group :disabled="loadingState === 'loading' || materialStore.loadingState !== 'success'">
        <v-chip
          @click="
            () => {
              date = dayjs(initialDate);
              getPdfHistory();
            }
          "
          >{{ i18n.global.t("global.dates.yesterday") }}</v-chip
        >
        <v-chip
          @click="
            () => {
              date = dayjs(initialDate).subtract(6, 'days').startOf('week').add(1, 'day');
              getPdfHistory();
            }
          "
          >{{ i18n.global.t("global.dates.lastWeek") }}</v-chip
        >
        <v-chip
          @click="
            () => {
              date = dayjs(initialDate).add(1,'day').subtract(1, 'month').startOf('month');
              getPdfHistory();
            }
          "
          >{{ i18n.global.t("global.dates.lastMonth") }}</v-chip
        >
      </v-chip-group>
    </div>
    <v-data-table
      :hover="loadingState !== 'loading'"
      class="mt-8"
      :loading="loadingState === 'loading'"
      item-value="raw"
      :headers="[
        {
          title: i18n.global.t('global.layout'),
          key: 'pdf',
          sortRaw(a, b) {
            return a.pdf.item > b.pdf.item ? 1 : -1;
          },
        },
        {
          title: i18n.global.t('global.date'),
          key: 'date',
          align: 'end',
        },
        {
          title: '',
          key: 'download',
          sortable: false,
          width: 0,
        },
      ]"
      :items="pdfSet"
    >
      <template v-slot:item.pdf="{ item }">
        <v-list-item
          class="px-0 min-w-64"
          :title="`${item.Quantity}x ${item.Layout}`"
          :subtitle="
            item.MaterialDescription === item.Layout
              ? item.Layout
              : item.MaterialDescription
              ? item.MaterialDescription
              : i18n.global.t('global.noDescription')
          "
        ></v-list-item>
      </template>
      <template v-slot:item.download="{ item }">
        <DownloadButton @download="() => (item.isNew = false)" :label-data="item"></DownloadButton>
      </template>
      <template v-slot:item.date="{ item }">
        <v-list-item
          class="px-0 min-w-32"
          :subtitle="`${getDateDiffText(Number(item.Date))} (${formatUNIX(item.Date)})`"
        ></v-list-item>
      </template>
      <template v-slot:loading>
        <div v-for="index in 4" :key="index">
          <div class="flex justify-between flex-row">
            <div class="flex">
              <v-skeleton-loader type="avatar"></v-skeleton-loader>
              <v-skeleton-loader class="min-w-64" type="list-item-two-line"></v-skeleton-loader>
            </div>
            <v-skeleton-loader class="min-w-36" type="text@1"></v-skeleton-loader>
          </div>
          <v-skeleton-loader class="min-w-36" type="divider"></v-skeleton-loader>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script setup lang="ts">
// modules
import { Ref, ref, onMounted, watch, computed } from "vue";
import dayjs, { Dayjs } from "dayjs";
// helpers
import { formatSAPDate } from "@/helpers/dateHelper";
import { formatUNIX, getDateDiffText } from "@/helpers/dateHelper";
import { i18n } from "@/locales/index";
import * as ApiResponse from "@/api-client/responses";
// components
import DownloadButton from "./DownloadButton.vue";
// instances
import { ApiClient } from "@/api-client";
import { useApplicationStore, useLabelStore, useMaterialStore, useSupplierStore, useSystemStore } from "@/store";

const showDateSelector = ref(false);
// get instances
const supplierStore = useSupplierStore();
const systemStore = useSystemStore();
const materialStore = useMaterialStore();
const labelStore = useLabelStore();
const applicationStore = useApplicationStore();
const apiClient = ApiClient.getInstance();

const selectedChip = ref("");

const loadingState = ref<"unset" | "loading" | "failed" | "success">("unset");
const abortController = ref(new AbortController());

const pdfSet = ref<NewAndOldPdfIndicator[]>([]);
interface NewAndOldPdfIndicator extends ApiResponse.PrintItems.PrintItemPdf {
  isNew: boolean;
}

const initialDate = dayjs().hour(0).minute(0).second(0).millisecond(0).subtract(1, "day");
const date = ref(initialDate);
const displayDateDay = computed(() => {
  const dayText = getDateDiffText(date.value);
  if (!dayText) return "";
  return `(${dayText})`;
});

const lastFilter = ref(initialDate);
const lastResults = ref<ApiResponse.PrintItems.GetPrintItemsPdf>([]);
const showIncompleteGenerationInfo = ref(false);

applicationStore.triggerPdfSearch.addListener("search", () => {
  getPdfHistory();
});

materialStore.onAbort.addListener("abort", () => {
  abortController.value.abort();
  abortController.value = new AbortController();
  pdfSet.value = [];
});

// get labels
watch(
  () => materialStore.loadingState,
  (state) => {
    if (state === "loading") {
      loadingState.value = "loading";
      return;
    }
    if (state === "failed") {
      loadingState.value = "failed";
      pdfSet.value = [];
      return;
    }
    if (state !== "success") return;

    start();
  }
);

async function start() {
  await getPdfHistory(true);
}
const allowedDates = (val: unknown) => {
  return true;
};

async function getPdfHistory(onMount?: boolean) {
  pdfSet.value = [];

  const startDate = formatSAPDate(date.value.toDate());

  loadingState.value = "loading";
  showDateSelector.value = false;
  labelStore.indicateTimeout = false;
  showIncompleteGenerationInfo.value = false;

  if (!supplierStore.selectedSupplier || !systemStore.selectedSystem || !materialStore.selectedMatNr) {
    console.warn(`No Supplier selected!`);
    return;
  }

  const material: string = materialStore.selectedMatNr;
  const supplier: string = supplierStore.selectedSupplier.SupplierId;
  const originalSupplier: string = systemStore.selectedSystem.supplierId;
  const system: string = systemStore.selectedSystem.system;

  let _rawPdfSet;
  try {
    _rawPdfSet = await apiClient.getPdfHistory(
      material,
      supplier,
      originalSupplier,
      system,
      startDate,
      abortController.value.signal
    );
  } catch (error: unknown) {
    if (error instanceof Error && error?.name == "AbortError") return;

    useApplicationStore().addAlert({
      key: `app.pdfHistory.loadingData.error`,
      keyData: {
        message: error instanceof Error ? error.message : String(error),
      },
      type: "Alert",
    });
    loadingState.value = "failed";

    showDateSelector.value = false;
    return;
  }

  pdfSet.value = processEntries(_rawPdfSet, onMount ?? false);

  const newEntries = pdfSet.value.filter((e) => e.isNew).length;

  if (newEntries < labelStore.newLabelCountBeforeHistoryChecked) {
    showIncompleteGenerationInfo.value = true;
  }

  // sort by date
  pdfSet.value.sort((a, b) => {
    if (a.Date !== b.Date) return Number(b.Date) - Number(a.Date);

    return b.PrintItemId > a.PrintItemId ? 1 : -1;
  });

  showDateSelector.value = false;
  loadingState.value = "success";

  lastResults.value = _rawPdfSet;
  lastFilter.value = date.value;
}

function processEntries(data: ApiResponse.PrintItems.GetPrintItemsPdf, isFirstTime: boolean): NewAndOldPdfIndicator[] {
  // return when it is the first time
  if (isFirstTime) return data.map((e) => ({ ...e, isNew: false }));

  // when filter was set before previous filter, then split and just check the latest entries
  if (date.value.isBefore(lastFilter.value)) {
    const _before = data.filter((e) => dayjs(e.Date).isBefore(lastFilter.value));
    const _after = data.filter((e) => dayjs(e.Date).isAfter(lastFilter.value));

    const after = _after.map((e) => ({
      ...e,
      isNew: !lastResults.value.find((e2) => e2.PrintItemId === e.PrintItemId),
    }));

    return [..._before.map((e) => ({ ...e, isNew: false })), ...after];
  }

  return data.map((e) => ({
    ...e,
    isNew: !lastResults.value.find((e2) => e2.PrintItemId === e.PrintItemId),
  }));
}
</script>

<style scoped lang="scss"></style>
